// Virtual keyboard handling (Phase 11, spec §11.9 "composer fixed with safe-area padding"). Android Chrome resizes
// the layout viewport itself (`interactive-widget=resizes-content` in index.html), iOS Safari only shrinks the
// *visual* viewport and lets the keyboard cover fixed / 100dvh layouts. The VisualViewport API tells us how much is
// covered: `--app-height` is the visible height and `--kb-inset` the keyboard overlap, so the shell (and with it the
// chat composer at its bottom) stays above the keyboard. `html[data-keyboard]` hides the bottom navigation meanwhile.

export interface ViewportSample {
  innerHeight: number;
  vvHeight: number;
  vvOffsetTop: number;
}

/** Height of the layout viewport hidden below the visual viewport (the on-screen keyboard), in px. */
export function keyboardInset({ innerHeight, vvHeight, vvOffsetTop }: ViewportSample): number {
  const covered = innerHeight - vvHeight - vvOffsetTop;
  // < 80 px is browser chrome (URL bar collapsing), not a keyboard
  return covered >= 80 ? Math.round(covered) : 0;
}

let installed = false;

export function installViewportVars(win: Window = window): () => void {
  if (installed || typeof win.visualViewport === 'undefined' || !win.visualViewport) return () => undefined;
  installed = true;
  const vv = win.visualViewport;
  const root = win.document.documentElement;
  let frame = 0;
  const apply = () => {
    frame = 0;
    const inset = keyboardInset({ innerHeight: win.innerHeight, vvHeight: vv.height, vvOffsetTop: vv.offsetTop });
    root.style.setProperty('--kb-inset', `${inset}px`);
    root.style.setProperty('--app-height', `${Math.round(vv.height + (inset ? 0 : vv.offsetTop))}px`);
    if (inset) {
      root.setAttribute('data-keyboard', '');
      // iOS scrolls the (non-scrollable) document to reveal the focused field; keep the shell pinned at the top
      if (win.scrollY) win.scrollTo(0, 0);
    } else root.removeAttribute('data-keyboard');
  };
  const schedule = () => {
    if (!frame) frame = win.requestAnimationFrame(apply);
  };
  vv.addEventListener('resize', schedule);
  vv.addEventListener('scroll', schedule);
  apply();
  return () => {
    vv.removeEventListener('resize', schedule);
    vv.removeEventListener('scroll', schedule);
    if (frame) win.cancelAnimationFrame(frame);
    installed = false;
  };
}
